import moment, { Moment } from "moment"
import React, { forwardRef, ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import "moment-timezone"
import {
  DateRangeFormatFunction,
  Event,
  EventProps,
  Formats,
  Calendar as ReactBigCalendar,
  CalendarProps as ReactBigCalendarProps,
  ToolbarProps,
  View,
  momentLocalizer,
} from "react-big-calendar"

import "react-big-calendar/lib/css/react-big-calendar.css"
import { LeftIcon, RightIcon } from "@assets/icons"
import MonoDatePicker from "@components/elements/DatePicker/MonoDatePicker"
import useAuth from "@hooks/useAuth"
import CalendarIcons from "@icons/CalendarIcons"
import UserIcon from "@icons/User"

import { Spinner } from "flowbite-react"

import { isDayPast } from "@mono/essentials/utils/Common"
import { getDomainTimezone } from "@mono/essentials/utils/TenantHelper"

import styles from "./MonoCalendar.module.css"

interface MonoCalendarProps extends Omit<ReactBigCalendarProps, "localizer"> {
  wrapperClassName?: string
  filters?: ReactNode
  filterPlaceholderClassName?: string
  isEventsLoading?: boolean
  eventsFormatter?: (currentView: string) => Event[]
  showBooked?: boolean
  showAvailable?: boolean
  hasFiltersData?: boolean
  hideSpilledDates?: boolean
}

moment.locale("en-US")
const tz = getDomainTimezone()
if (tz) {
  moment.tz.setDefault(tz)
}
const localizer = momentLocalizer(moment)

const rangeDateFormatter: DateRangeFormatFunction = ({ start, end }, culture, localizer) =>
  localizer
    ? `${localizer.format(start, "MMMM DD, YYYY", culture || "")} — ${localizer.format(
        end,
        "MMMM DD, YYYY",
        culture || ""
      )}`
    : ""

const formats: Formats = {
  dateFormat: "D",
  weekdayFormat: "ddd",
  dayFormat: "ddd D/M",
  monthHeaderFormat: "MMMM YYYY",
  dayHeaderFormat: "MMMM DD, YYYY",
  dayRangeHeaderFormat: rangeDateFormatter,
}

const MonoCalendar = ({
  className,
  style,
  wrapperClassName,
  filters,
  filterPlaceholderClassName,
  onRangeChange,
  onSelectEvent,
  events,
  isEventsLoading = false,
  eventsFormatter,
  showBooked = false,
  showAvailable = false,
  hasFiltersData,
  hideSpilledDates = false,
  ...rest
}: MonoCalendarProps) => {
  const { domainDetails } = useAuth()
  const [currentView, setCurrentView] = useState<View>("month")
  const [viewingDate, setViewingDate] = useState<Date>(new Date())
  const [selectedDate, setSelectedDate] = useState(moment())

  const onNavigate = useCallback((newDate: Date, view) => setSelectedDate(moment(newDate)), [])

  const MonoCalendarHeader = ({ label, onNavigate, onView, view, date }: ToolbarProps) => {
    const customTextInput = forwardRef((props, ref) => {
      useEffect(() => {
        if (view === "month" && date.toDateString() !== viewingDate.toDateString()) {
          setViewingDate(date)
        }
      }, [date, view, viewingDate])

      return (
        <h2
          className={"cursor-pointer calendarTitle text-neutral-900"}
          onClick={(props as unknown as any).onClick}
        >
          {label}
        </h2>
      )
    })

    return (
      <div className={"flex flex-col gap-4"}>
        <div className={"flex flex-row items-center justify-between w-full"}>
          <div className={"flex flex-row items-center gap-2"}>
            <button className={"text16Medium text-primary-600"} onClick={() => onNavigate("TODAY")}>
              Today
            </button>
            <button onClick={() => onNavigate("PREV")}>
              <LeftIcon width={20} height={20} className={"text-neutral-900"} />
            </button>
            <MonoDatePicker
              customTextInput={customTextInput}
              selected={selectedDate?.toDate()}
              onChange={(date: Date) => onNavigate("DATE", date)}
            />
            <button onClick={() => onNavigate("NEXT")}>
              <RightIcon width={20} height={20} className={"text-neutral-900"} />
            </button>
          </div>
          {isEventsLoading ? (
            <div
              className={
                "flex items-center justify-start gap-2 text14Regular text-primary-600 w-[125px]"
              }
            >
              <Spinner size={"sm"} light />
              Loading events
            </div>
          ) : (
            ""
          )}
          <div
            className={
              "flex h-9.5 flex-row items-center overflow-hidden rounded-lg border border-neutral-200"
            }
          >
            <button
              className={`${
                currentView === "month"
                  ? "text14Medium bg-primary-100 text-neutral-900"
                  : "text14Regular bg-whiteBlack-100 text-neutral-600"
              } h-full w-20`}
              onClick={() => {
                onView("month")
                setCurrentView("month")
              }}
            >
              Month
            </button>
            <button
              className={`
                ${
                  currentView === "week"
                    ? "text14Medium bg-primary-100 text-neutral-900"
                    : "text14Regular bg-whiteBlack-100 text-neutral-600"
                }
                h-full w-20 border-x border-neutral-200
              `}
              onClick={() => {
                onView("week")
                setCurrentView("week")
              }}
            >
              Week
            </button>
            <button
              className={`${
                currentView === "day"
                  ? "text14Medium bg-primary-100 text-neutral-900"
                  : "text14Regular bg-whiteBlack-100 text-neutral-600"
              } h-full w-20`}
              onClick={() => {
                onView("day")
                setCurrentView("day")
              }}
            >
              Day
            </button>
          </div>
        </div>
        <div className={`${filterPlaceholderClassName ?? ""} h-[72px] w-full bg-transparent`} />
        {/* {filters ?? ''} */}
      </div>
    )
  }

  const MonoCalendarEvent = ({ title, event }: EventProps) => {
    return (
      <div
        className={`
          ${hideSpilledDates && viewingDate.getMonth() !== event.start.getMonth() && currentView === "month" ? "hidden" : ""}
          ${
            isDayPast(event.resource.availability.slot_date)
              ? "border-neutral-400 bg-neutral-100"
              : event.resource.availability.available <= 0
                ? "border-red-500 bg-red-200"
                : "border-green-600 bg-green-200"
          }
          ${currentView !== "day" ? "mx-[5px]" : "h-full !items-start"}
          text12Medium mb-[3px] flex flex-col items-start justify-start rounded-md border-l-2 px-1 py-1.5 gap-1 text-neutral-700
        `}
      >
        <div className={"flex flex-row flex-wrap items-center justify-start w-full gap-1"}>
          <p className={"max-w-full whitespace-break-spaces"}>{title}</p>
          {showBooked && event.resource.availability.booked ? (
            <div className={"flex flex-row items-center"}>
              {event.resource.availability.available === 0 ? (
                <div
                  className={`
                    mr-0.5 min-h-[5px] min-w-[5px] rounded-full
                    ${isDayPast(event.resource.availability.slot_date) ? "bg-neutral-600" : "bg-red-600"}
                  `}
                />
              ) : (
                <UserIcon
                  className={`mr-0.5 ${
                    isDayPast(event.resource.availability.slot_date)
                      ? "text-neutral-600"
                      : "text-green-800"
                  }`}
                  width={12}
                  height={12}
                />
              )}
              <span
                className={`
                  text12Regular
                  ${
                    isDayPast(event.resource.availability.slot_date)
                      ? "text-neutral-600"
                      : event.resource.availability.available === 0
                        ? "text-red-600"
                        : "text-green-800"
                  }
                `}
              >
                {event.resource.availability.booked}
              </span>
            </div>
          ) : null}

          {showAvailable && event.resource.availability.available ? (
            <div className={"flex flex-row items-center"}>
              {event.resource.availability.available === 0 ? (
                <div
                  className={`
                    mr-0.5 min-h-[5px] min-w-[5px] rounded-full
                    ${isDayPast(event.resource.availability.slot_date) ? "bg-neutral-600" : "bg-red-600"}
                  `}
                />
              ) : (
                <CalendarIcons
                  className={`mr-0.5 ${
                    isDayPast(event.resource.availability.slot_date)
                      ? "text-neutral-600"
                      : "text-green-800"
                  }`}
                  width={12}
                  height={12}
                />
              )}
              <span
                className={`
                  text12Regular
                  ${
                    isDayPast(event.resource.availability.slot_date)
                      ? "text-neutral-600"
                      : event.resource.availability.available === 0
                        ? "text-red-600"
                        : "text-green-800"
                  }
                `}
              >
                {event.resource.availability.available}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  const formattedEvents = useMemo(() => {
    if (eventsFormatter) {
      return eventsFormatter(currentView)
    }
    return events
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, events])

  return (
    <div
      className={`
        ${isEventsLoading ? styles.loading : ""}
        ${
          currentView === "month"
            ? styles.monthViewWrapper
            : currentView === "week"
              ? styles.weekViewWrapper
              : styles.dayViewWrapper
        }
        ${styles.monoCalendarWrapper} ${wrapperClassName ?? ""} h-full
        ${hideSpilledDates ? styles.hideSpilledDates : ""}
      `}
    >
      {filters || ""}
      <ReactBigCalendar
        {...rest}
        date={selectedDate?.toDate()}
        events={formattedEvents}
        className={className}
        localizer={localizer}
        formats={formats}
        startAccessor={"start"}
        endAccessor={"end"}
        views={["month", "week", "day"]}
        step={30}
        style={{ ...style, height: "100%" }}
        messages={{ next: "›", previous: "‹", today: "Today" }}
        components={{
          toolbar: MonoCalendarHeader,
          event: MonoCalendarEvent,
          eventWrapper: ({ children, event }: any) =>
            currentView === "day" ? (
              <div
                className={
                  isDayPast(event.resource.availability.slot_date) &&
                  !domainDetails.allow_past_bookings
                    ? "pointer-events-none"
                    : ""
                }
              >
                {children}
              </div>
            ) : (
              <div
                className={
                  isDayPast(event.resource.availability.slot_date) &&
                  !domainDetails.allow_past_bookings
                    ? "pointer-events-none"
                    : ""
                }
              >
                {children}
              </div>
            ),
        }}
        showAllEvents
        onShowMore={() => setCurrentView("day")}
        tooltipAccessor={undefined}
        onNavigate={onNavigate}
        onRangeChange={(range, view) => onRangeChange?.(range, view ?? currentView)}
        onSelectEvent={onSelectEvent}
        dayLayoutAlgorithm={currentView === "day" ? "no-overlap" : "overlap"}
      />
    </div>
  )
}

export default MonoCalendar
